import { Fragment, Suspense, lazy, useEffect, type ReactNode } from 'react';
import { SidebarProvider } from "@/context/SidebarContext";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider, useIsFetching, useIsMutating } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { hostPlatform, currentPlatform, PLATFORMS, B2C_PLATFORMS, platformPath, scopedPath, b2cPath, ePath, tPath, fPath, gPath, type Platform, type B2CPlatform } from "@/utils/paths";
import { currentBrandLabel, platformBrandLabel } from "@/config/resources";
import { platformPortalHome } from "@/config/entrepreneurPlatforms";
import { ThemeProvider } from "next-themes";
import { useTranslation } from "react-i18next";
import { ErrorBoundary, SectionErrorFallback } from "@/components/errors";
import { CommandPalette } from "./components/search/CommandPalette";
import { BackgroundTaskManager } from "./components/shared/BackgroundTaskManager";
import { ProcessingOverlayRoot } from "./components/shared/ProcessingOverlayRoot";
import { InfoPanelRoot } from "./components/shared/InfoPanelRoot";
import { SidePanelRoot } from "./components/shared/SidePanelRoot";
import { EmoiBubble } from "./components/emoi/EmoiBubble";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { useAuthStore, useEntitledPlatforms } from "./stores/authStore";
import { useGatedLoading } from "./stores/processingOverlayStore";
import { DynamicSidebar } from "./components/sidebar/DynamicSidebar";
import { SidebarLayout } from "./components/layouts/SidebarLayout";
import { StaffRoute, AnyStaffRoute, EntrepreneurAuthWrapper, AccessDeniedPage } from "./components/auth/AuthWrapper";
import { GaiaParticipantRoute } from "./components/auth/GaiaParticipantRoute";
import { FoundersHubParticipantRoute } from "./components/auth/FoundersHubParticipantRoute";
import { ApprovedOnlyGuard } from "./components/auth/StateGuard";
import "./i18n";
import { queryClient } from "@/lib/queryClient";
import { useSectors } from "@/hooks/useSectors";
import { useRoleRegistry } from "@/hooks/useRoleRegistry";
import LoadingBar from "@/components/LoadingBar";
import { PageSkeleton } from "@/components/PageSkeleton";

// Lazy load page components
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Startup pages
const AllStartups = lazy(() => import("./pages/startups/AllStartups"));
const StartupDetail = lazy(() => import("./pages/startups/StartupDetail"));
const DiscoveredStartups = lazy(() => import("./pages/startups/DiscoveredStartups"));
const DiscoveredStartupDetail = lazy(() => import("./pages/startups/DiscoveredStartupDetail"));

// Market Insight pages
const AllMarketInsights = lazy(() => import("./pages/market-insights/AllMarketInsights"));
const CreateMarketInsightPage = lazy(() => import("./pages/market-insights/CreateMarketInsightPage"));
const MarketInsightDetailsPage = lazy(() => import("./pages/market-insights/MarketInsightDetailsPage").then(m => ({ default: m.MarketInsightDetailsPage })));

// Opportunities pages
const AllOpportunities = lazy(() => import("./pages/opportunities/AllOpportunities"));
const NewOpportunityPage = lazy(() => import("./pages/opportunities/NewOpportunityPage"));
const NewSolutionPage = lazy(() => import("./pages/opportunities/NewSolutionPage"));
const OpportunityDetailPageAdmin = lazy(() => import("./pages/opportunities/OpportunityDetailPage").then(m => ({ default: m.OpportunityDetailPage })));
const EditOpportunityPage = lazy(() => import("./pages/opportunities/EditOpportunityPage"));
const SolutionDetailPage = lazy(() => import("./pages/opportunities/SolutionDetailPage").then(m => ({ default: m.SolutionDetailPage })));

// Committees pages
const CommitteesListPage = lazy(() => import("./pages/committees/CommitteesListPage"));
const CommitteeDetailPage = lazy(() => import("./pages/committees/CommitteeDetailPage"));

// Projects pages
const ProjectLeadsListPage = lazy(() => import("./pages/shaping/ProjectLeadsListPage"));
const ProjectLeadDetailPage = lazy(() => import("./pages/shaping/ProjectLeadDetailPage"));

const ProjectsListPage = lazy(() => import("./pages/projects/ProjectsListPage"));
const ProjectViewPage = lazy(() => import("./pages/projects/ProjectViewPage"));

// Clients pages
const ClientsListPage = lazy(() => import("./pages/clients/ClientsListPage"));
const ClientDetailPage = lazy(() => import("./pages/clients/ClientDetailPage"));

// Applications pages
const AllApplicationsView = lazy(() => import("./components/applications/AllApplicationsView"));
const PendingApplications = lazy(() => import("./pages/foundershub/applications/PendingApplications"));
const ApprovedApplications = lazy(() => import("./pages/foundershub/applications/ApprovedApplications"));
const RejectedApplications = lazy(() => import("./pages/foundershub/applications/RejectedApplications"));
const ApplicationProfilePageNew = lazy(() => import("./pages/admin/ApplicationProfilePageNew"));

// FoundersHub Projects pages
const FoundersHubProjectsListPage = lazy(() => import("./pages/foundershub/projects/ProjectsListPage"));
const FoundersHubProjectDetailPage = lazy(() => import("./pages/foundershub/projects/ProjectDetailPage"));

// Auth pages
const EntrepreneurLoginPage = lazy(() => import("./pages/entrepreneurs/LoginPage").then(m => ({ default: m.EntrepreneurLoginPage })));
const ConsentPage = lazy(() => import("./pages/entrepreneurs/ConsentPage").then(m => ({ default: m.ConsentPage })));
const LegalDocumentPage = lazy(() => import("./pages/legal/LegalDocumentPage"));
const OnboardingPage = lazy(() => import("./pages/entrepreneurs/OnboardingPage").then(m => ({ default: m.OnboardingPage })));
const WaitingPage = lazy(() => import("./pages/entrepreneurs/WaitingPage").then(m => ({ default: m.WaitingPage })));
const DashboardPage = lazy(() => import("./pages/entrepreneurs/DashboardPage").then(m => ({ default: m.DashboardPage })));
const EntrepreneurOpportunityDetailPage = lazy(() => import("./pages/entrepreneurs/OpportunityDetailPage").then(m => ({ default: m.EntrepreneurOpportunityDetailPage })));
const IndustriesPage = lazy(() => import("./pages/entrepreneurs/IndustriesPage").then(m => ({ default: m.IndustriesPage })));
const IndustryDetailPage = lazy(() => import("./pages/entrepreneurs/IndustryDetailPage").then(m => ({ default: m.IndustryDetailPage })));
const MyApplicationsPage = lazy(() => import("./pages/entrepreneurs/MyApplicationsPage").then(m => ({ default: m.MyApplicationsPage })));
const PersonProfilePage = lazy(() => import("./pages/people/PersonProfilePage"));
const MatchedOpportunitiesPage = lazy(() => import("./pages/entrepreneurs/MatchedOpportunitiesPage").then(m => ({ default: m.MatchedOpportunitiesPage })));
const MatchedOpportunityDetailPage = lazy(() => import("./pages/entrepreneurs/MatchedOpportunityDetailPage").then(m => ({ default: m.MatchedOpportunityDetailPage })));
const MyOpportunitiesPage = lazy(() => import("./pages/entrepreneurs/MyOpportunitiesPage").then(m => ({ default: m.MyOpportunitiesPage })));
const CreateOpportunityPage = lazy(() => import("./pages/entrepreneurs/CreateOpportunityPage").then(m => ({ default: m.CreateOpportunityPage })));
const EditMyOpportunityPage = lazy(() => import("./pages/entrepreneurs/EditMyOpportunityPage").then(m => ({ default: m.EditMyOpportunityPage })));
const MyOpportunityDetailPage = lazy(() => import("./pages/entrepreneurs/MyOpportunityDetailPage").then(m => ({ default: m.MyOpportunityDetailPage })));
const VerifyEmailPage = lazy(() => import("./pages/entrepreneurs/VerifyEmailPage").then(m => ({ default: m.VerifyEmailPage })));

// Admin pages
const StaffLoginPageComponent = lazy(() => import("./pages/staff/LoginPage").then(m => ({ default: m.StaffLoginPage })));
const AdminSuccessPage = lazy(() => import("./pages/admin/AdminSuccessPage").then(m => ({ default: m.AdminSuccessPage })));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage").then(m => ({ default: m.AdminDashboardPage })));
const FoundersHubLandingPage = lazy(() => import("./pages/admin/FoundersHubLandingPage").then(m => ({ default: m.FoundersHubLandingPage })));

// Gaia pages
const ProgramsPage = lazy(() => import("./pages/gaia/ProgramsPage"));
const ProgramDetailPage = lazy(() => import("./pages/gaia/ProgramDetailPage"));
const GaiaProjectDetailPage = lazy(() => import("./pages/gaia/GaiaProjectDetailPage"));
const GaiaPhaseDetailPage = lazy(() => import("./pages/gaia/GaiaPhaseDetailPage"));
const GaiaProjectsPage = lazy(() => import("./pages/gaia/GaiaProjectsPage"));
const GaiaMeetingsPage = lazy(() => import("./pages/gaia/GaiaMeetingsPage"));
const GaiaDocumentsPage = lazy(() => import("./pages/gaia/GaiaDocumentsPage"));
const GaiaDocumentDetailPage = lazy(() => import("./pages/gaia/GaiaDocumentDetailPage"));
const GaiaMyProjectsPage = lazy(() => import("./pages/gaia/participant/GaiaMyProjectsPage"));
const GaiaMyProjectPage = lazy(() => import("./pages/gaia/participant/GaiaMyProjectPage"));
const GaiaMyMeetingsPage = lazy(() => import("./pages/gaia/participant/GaiaMyMeetingsPage"));
const GaiaMyPhasePage = lazy(() => import("./pages/gaia/participant/GaiaMyPhasePage"));
const EntrepreneurProfilePageNew = lazy(() => import("./pages/admin/EntrepreneurProfilePageNew"));
const MicrosoftMockPage = lazy(() => import("./pages/admin/MicrosoftMockPage").then(m => ({ default: m.MicrosoftMockPage })));
const DashboardRedirect = lazy(() => import("./pages/admin/DashboardRedirect").then(m => ({ default: m.DashboardRedirect })));

// Organization pages — one tree at /org, mounted per served platform
// (fase 7, §8.1). OrgExpertsPage, OrgMentorsPage and OrgUsersPage are gone:
// their tabs fold into OrgTeamPage as derived, read-only state (§8.3).
// Membership is gone too: platform badges live on the team roster, and
// PlatformOrgPage (trimmed to settings-only) is now reached directly via
// /org/platform, gated on org_platform_<platform>, instead of a selector page.
const PlatformOrgPage = lazy(() => import("./pages/org/PlatformOrgPage"));
const OrgDashboardPage = lazy(() => import("./pages/org/OrgDashboardPage"));
const OrgTeamPage = lazy(() => import("./pages/org/OrgTeamPage"));
const OrgLocationsPage = lazy(() => import("./pages/org/OrgLocationsPage"));
const OrgSourcesPage = lazy(() => import("./pages/org/OrgSourcesPage"));
const ExpertOnboardingPage = lazy(() => import("./pages/experts/ExpertOnboardingPage"));
const MentorOnboardingPage = lazy(() => import("./pages/mentors/MentorOnboardingPage"));

// Superadmin pages
const SuperAdminUsersPage = lazy(() => import("./pages/superadmin/SuperAdminUsersPage"));
const OrganizationsPage = lazy(() => import("./pages/superadmin/OrganizationsPage"));
const RolesPermissionsPage = lazy(() => import("./pages/superadmin/RolesPermissionsPage"));
const OrganizationDetailPage = lazy(() => import("./pages/superadmin/OrganizationDetailPage"));

// User detail page
const StaffUserDetailPage = lazy(() => import("./pages/users/StaffUserDetailPage"));

// Reset password page
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));

// Not-invited page (staff OAuth rejection)
const AuthSuccessPage = lazy(() => import("./pages/auth/AuthSuccessPage").then(m => ({ default: m.AuthSuccessPage })));
const LinkedInMockPage = lazy(() => import("./pages/auth/LinkedInMockPage").then(m => ({ default: m.LinkedInMockPage })));
const SignupPage = lazy(() => import("./pages/auth/SignupPage").then(m => ({ default: m.SignupPage })));
const VerifyEmailSignupPage = lazy(() => import("./pages/auth/VerifyEmailSignupPage").then(m => ({ default: m.VerifyEmailSignupPage })));
const NotInvitedPage = lazy(() => import("./pages/auth/NotInvitedPage").then(m => ({ default: m.NotInvitedPage })));

// Shared pages
const NotificationsPage = lazy(() => import("./pages/NotificationsPage").then(m => ({ default: m.NotificationsPage })));

// Page loading fallback — full-viewport skeleton so the footer doesn't float mid-screen
const PageLoader = () => <PageSkeleton />;

/** Chrome for `/` and the catch-all. It wears the brand of the platform we are
 * on; where the URL belongs to no platform it stays neutral instead of
 * borrowing terminus's identity, and the switcher lists every area. */
const ShellSidebar = () => {
  const platform = currentPlatform();
  return <DynamicSidebar area={platform ?? 'org'} brandLabel={platformBrandLabel(platform)} hideMenu />;
};

/** One B2C shell for both platforms. It owns the auth boundary, the membership
 * guard and the chrome; each platform only supplies its own content routes.
 * The portal lives at `/entrepreneur` INSIDE its platform — `/entrepreneurs`
 * (plural) is the staff area that manages them. */
const EntrepreneurPortal = ({
  platform,
  sidebar,
  children,
}: {
  platform: B2CPlatform;
  sidebar: ReactNode;
  children: ReactNode;
}) => {
  const Guard = platform === "gaia" ? GaiaParticipantRoute : FoundersHubParticipantRoute;
  return (
    <EntrepreneurAuthWrapper>
      <Guard>
        <SidebarLayout sidebar={sidebar}>
          <Routes>
            {children}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </SidebarLayout>
      </Guard>
    </EntrepreneurAuthWrapper>
  );
};

const FoundersHubPortal = () => (
  <EntrepreneurPortal platform="foundershub" sidebar={<DynamicSidebar area="entrepreneur" />}>
        <Route path="/" element={
          <ApprovedOnlyGuard>
            <DashboardPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/consent" element={<ConsentPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/waiting" element={<WaitingPage />} />
        <Route path="/dashboard" element={
          <ApprovedOnlyGuard>
            <DashboardPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/opportunity/:id" element={
          <ApprovedOnlyGuard>
            <EntrepreneurOpportunityDetailPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/matched-opportunities" element={
          <ApprovedOnlyGuard>
            <MatchedOpportunitiesPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/matched-opportunity/:id" element={
          <ApprovedOnlyGuard>
            <MatchedOpportunityDetailPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/industries" element={
          <ApprovedOnlyGuard>
            <IndustriesPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/industries/:sector" element={
          <ApprovedOnlyGuard>
            <IndustryDetailPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/my-opportunities" element={
          <ApprovedOnlyGuard>
            <MyOpportunitiesPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/my-opportunities/new" element={
          <ApprovedOnlyGuard>
            <CreateOpportunityPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/my-opportunities/:id/edit" element={
          <ApprovedOnlyGuard>
            <EditMyOpportunityPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/my-opportunities/:id" element={
          <ApprovedOnlyGuard>
            <MyOpportunityDetailPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/applications" element={
          <ApprovedOnlyGuard>
            <MyApplicationsPage />
          </ApprovedOnlyGuard>
        } />
        <Route path="/profile" element={<Navigate to={ePath('/me')} replace />} />
        <Route path="/me" element={
          <ApprovedOnlyGuard>
            <PersonProfilePage participant />
          </ApprovedOnlyGuard>
        } />
        <Route path="/notifications" element={
          <ApprovedOnlyGuard>
            <NotificationsPage />
          </ApprovedOnlyGuard>
        } />
  </EntrepreneurPortal>
);

const GaiaPortal = () => (
  <EntrepreneurPortal
    platform="gaia"
    sidebar={
      <DynamicSidebar
        area="gaia"
        assignedResourceKeys={["gaia_projects", "gaia_meetings"]}
        routeOverrides={{ gaia_projects: platformPortalHome('gaia'), gaia_meetings: ePath('/meetings') }}
      />
    }
  >
    <Route path="/" element={<Navigate to={ePath('/my-projects')} replace />} />
    <Route path="/dashboard" element={<Navigate to={ePath('/my-projects')} replace />} />
    <Route path="/my-projects" element={<GaiaMyProjectsPage />} />
    <Route path="/my-projects/:projectId" element={<GaiaMyProjectPage />} />
    <Route path="/my-projects/:projectId/phases/:phaseId" element={<GaiaMyPhasePage />} />
    <Route path="/meetings" element={<GaiaMyMeetingsPage />} />
    <Route path="/profile" element={<Navigate to={ePath('/me')} replace />} />
    <Route path="/me" element={<PersonProfilePage participant />} />
    <Route path="/documents/:documentId" element={<GaiaDocumentDetailPage participant />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </EntrepreneurPortal>
);

/** Gaia participants used to live at `/my-projects`, sharing a root with the
 * staff tree. Forward the whole sub-path into their own portal. */
const GaiaParticipantPathRedirect = () => {
  const { "*": rest } = useParams();
  return <Navigate to={ePath(`/my-projects${rest ? `/${rest}` : ""}`)} replace />;
};

// Modo prefijo: `/org` y `/superadmin` sin plataforma delante no son de nadie.
// Reenvían al panel de la primera plataforma a la que el usuario tiene
// derecho, preservando la sub-ruta, para que un marcador viejo siga valiendo.
//
// 🔴 `window.location.replace` y NO <Navigate>: las rutas del catálogo
// (`config/resources.ts`) y los hrefs del switcher de ambos paneles se
// calculan UNA VEZ al cargar el módulo, a partir de `currentPlatform()` — algo
// seguro solo porque cruzar de prefijo de plataforma cuesta siempre una carga
// completa de documento. Una navegación SPA aquí dejaría cada <Link> del
// sidebar apuntando al prefijo anterior — rebote infinito por este mismo
// redirect, y el item activo sin marcar nunca. `openNotificationLink`
// (utils/notificationLink.ts) respeta el mismo invariante: solo entra a
// `navigate()` cuando el destino resuelto nombra la plataforma en la que ya
// estás.
const PlatformPanelRedirect = ({ panel }: { panel: '/org' | '/superadmin' }) => {
  const { '*': rest } = useParams();
  const { t } = useTranslation();
  const platforms = useEntitledPlatforms();
  const target = platforms[0];

  useEffect(() => {
    if (!target) return;
    const suffix = rest ? `/${rest}` : panel === '/org' ? '/overview' : '/organizations';
    window.location.replace(platformPath(target, `${panel}${suffix}`));
  }, [target, rest, panel]);

  // Zero entitled platforms means there is nowhere to redirect to — this is
  // access denial, not a missing page. Same 403 shape AuthWrapper renders for
  // a denied staff user, `area` left undefined: no platform's sidebar makes
  // sense for someone entitled to none. `href="/"` rather than
  // `platformHome(currentPlatform())`: with no entitled platform there is no
  // platform home, and `platformHome(null)` resolves to the org panel — the
  // very page being denied, which would make "return to dashboard" a dead
  // end. `/` is the neutral shell, the only sound landing place here.
  if (!target) {
    return (
      <AccessDeniedPage
        title={t('auth.wrapper.accessForbidden')}
        description={t('auth.wrapper.noPermission')}
        href="/"
        linkLabel={t('auth.wrapper.returnToDashboard')}
      />
    );
  }
  return null;
};

// /org/users(/:id) -> /org/team(/:id), scoped to the platform whose org tree
// matched.
const OrgTeamAliasRedirect = () => {
  const { "*": rest } = useParams();
  return <Navigate to={scopedPath(`/org/team${rest ? `/${rest}` : ""}`)} replace />;
};

// /org/platforms/gaia -> el panel de org DE gaia. Preserva la plataforma que
// el usuario estaba mirando en vez de dar 404 a un marcador real.
const LegacyPlatformSectionRedirect = () => {
  const { legacyPlatform } = useParams();
  const target = PLATFORMS.find((p) => p === legacyPlatform);
  if (!target) return <Navigate to={scopedPath('/org/overview')} replace />;
  window.location.replace(platformPath(target, '/org/platform'));
  return null;
};

// Mount emoi only inside the terminus tool AND only for an authenticated user.
// The bubble triggers admin-power tools via /api/emoi/chat (require_admin_scope),
// so showing it pre-login would just dead-end on 401 and confuse the user.
/** One build serves all three hosts, so the tab title cannot come from a
 * build-time variable — it is resolved from the platform at runtime. A leaf so
 * navigation re-renders this node and not the whole route table. */
const ENV_SUFFIX =
  import.meta.env.VITE_ENVIRONMENT && import.meta.env.VITE_ENVIRONMENT !== 'production'
    ? ` (${import.meta.env.VITE_ENVIRONMENT})`
    : '';

const DocumentTitle = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = `${currentBrandLabel()}${ENV_SUFFIX}`;
  }, [pathname]);
  return null;
};

const EmoiMount = () => {
  // Subscribe to navigation so `currentPlatform()` is re-read on every move.
  useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const emoiEnabled = useBooleanFlagValue("emoi_enabled", false);
  if (!emoiEnabled || currentPlatform() !== 'terminus' || !isAuthenticated || !user) return null;
  return <EmoiBubble />;
};

/** A platform serves its own routes at the root of its own host. Off a platform
 * host — local dev, bare domain — every platform is reachable under its own
 * prefix. What never happens is one platform's prefix on another's host. */
const servesPlatform = (platform: Platform) => hostPlatform === null || hostPlatform === platform;

/** Off a platform host every platform lives under its own prefix, so a bare
 * `/org` or `/superadmin` — no platform named — belongs to nobody. The
 * `PlatformPanelRedirect` mounts for those bare paths exist only here: on a
 * platform host that path already IS the platform's own panel. */
const inPrefixMode = hostPlatform === null;

/** The staff login carries the platform's brand, so in prefix mode it needs one
 * mount per platform. On a platform host the plain `/login` already knows. */
const LOGIN_PATHS = hostPlatform ? ['/login'] : ['/login', ...PLATFORMS.map((p) => platformPath(p, '/login'))];

/** Terminus has no founders, so it has no B2C section — the portal is mounted
 * only by the platforms that actually have one, under their own prefix.
 * Keyed by `B2CPlatform`, so adding one is a compile error here rather than a
 * silently missing route. */
const B2C_PORTALS: Record<B2CPlatform, () => JSX.Element> = {
  foundershub: FoundersHubPortal,
  gaia: GaiaPortal,
};

const B2C_MOUNTS = B2C_PLATFORMS.filter(servesPlatform);

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ── B2C portals, one per platform, sharing one shell. The auth
            screens carry no layout, and verification sits OUTSIDE the
            participant guard: that guard is what redirects a not-yet-active
            member here, so guarding it would loop. ── */}
        {B2C_MOUNTS.map((platform) => {
          const Portal = B2C_PORTALS[platform];
          const at = (path: string) => b2cPath(platform, path);
          return (
          <Fragment key={platform}>
            <Route path={at('/auth/success')} element={<AuthSuccessPage />} />
            <Route path={at('/auth/linkedin/mock')} element={<LinkedInMockPage />} />
            <Route path={at('/login')} element={<EntrepreneurLoginPage />} />
            <Route path={at('/signup')} element={<SignupPage />} />
            <Route path={at('/verify-email')} element={
              <EntrepreneurAuthWrapper>
                <VerifyEmailPage platform={platform} />
              </EntrepreneurAuthWrapper>
            } />
            <Route path={`${at('')}/*`} element={<Portal />} />
          </Fragment>
          );
        })}

        {/* ── Cross-platform staff surfaces ── mounted once at the root of
            whatever host we are on, never under a product prefix. Reached
            identically from every platform, exactly like /org below. ── */}
        <Route path="/auth/success" element={<AdminSuccessPage />} />
        <Route path="/auth/not-invited" element={<NotInvitedPage />} />
        <Route path="/auth/microsoft/mock" element={<MicrosoftMockPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailSignupPage />} />
        {LOGIN_PATHS.map((path) => (
          <Route key={path} path={path} element={<StaffLoginPageComponent />} />
        ))}
        <Route path="/profile" element={<Navigate to="/me" replace />} />
        <Route path="/me" element={
          <AnyStaffRoute>
            <SidebarLayout sidebar={<ShellSidebar />}>
              <PersonProfilePage />
            </SidebarLayout>
          </AnyStaffRoute>
        } />
        <Route path="/users/:id" element={
          <AnyStaffRoute>
            <SidebarLayout sidebar={<ShellSidebar />}>
              <PersonProfilePage />
            </SidebarLayout>
          </AnyStaffRoute>
        } />
        {/* Cross-platform like /me: the notification bell links here from
            every host, and staff had no `/notifications` mounted anywhere, so
            "view all" fell through to the catch-all NotFound. */}
        <Route path="/notifications" element={
          <AnyStaffRoute>
            <SidebarLayout sidebar={<ShellSidebar />}>
              <NotificationsPage />
            </SidebarLayout>
          </AnyStaffRoute>
        } />

        {/* ── Superadmin and organization panels ── one per served platform,
            exactly like the three product trees below: unchanged URLs on a
            platform host, /{platform}/org and /{platform}/superadmin in
            prefix mode. `/org` and `/superadmin` bare of a platform are not
            registered here — see the PlatformPanelRedirect mounts after this
            map — so `currentPlatform()` always resolves inside either tree. */}
        {PLATFORMS.filter(servesPlatform).map((platform) => (
          <Fragment key={`panels-${platform}`}>
            {/* ── Superadmin panel ── canonical /superadmin. It used to live
                inside the terminus tree, which meant it simply did not exist
                on the gaia or foundershub hosts. Its gate is the resource,
                not the host.

                One StaffRoute per section, like terminus and gaia: `sa_users`
                and `sa_roles` are catalogue keys of their own, so a role
                holding only one of them must be stopped at the doors it
                cannot open instead of walking in behind `sa_organizations`.
                That outer key stays as the panel's baseline — it is where
                /superadmin lands, and the sidebar layout moves inside each
                section so a denial renders ONE sidebar (AccessDeniedPage
                brings its own). ── */}
            <Route path={platformPath(platform, '/superadmin')} element={<Navigate to={platformPath(platform, '/superadmin/organizations')} replace />} />
            <Route path={`${platformPath(platform, '/superadmin')}/*`} element={
              <StaffRoute resource="sa_organizations">
                <Routes>
                  <Route path="/organizations" element={
                    <StaffRoute resource="sa_organizations">
                      <SidebarLayout sidebar={<DynamicSidebar area="superadmin" />}>
                        <OrganizationsPage />
                      </SidebarLayout>
                    </StaffRoute>
                  } />
                  <Route path="/organizations/:slug" element={
                    <StaffRoute resource="sa_organizations">
                      <SidebarLayout sidebar={<DynamicSidebar area="superadmin" />}>
                        <OrganizationDetailPage />
                      </SidebarLayout>
                    </StaffRoute>
                  } />
                  <Route path="/users" element={
                    <StaffRoute resource="sa_users">
                      <SidebarLayout sidebar={<DynamicSidebar area="superadmin" />}>
                        <SuperAdminUsersPage />
                      </SidebarLayout>
                    </StaffRoute>
                  } />
                  <Route path="/roles" element={
                    <StaffRoute resource="sa_roles">
                      <SidebarLayout sidebar={<DynamicSidebar area="superadmin" />}>
                        <RolesPermissionsPage />
                      </SidebarLayout>
                    </StaffRoute>
                  } />
                  <Route path="*" element={
                    <SidebarLayout sidebar={<DynamicSidebar area="superadmin" />}>
                      <NotFound />
                    </SidebarLayout>
                  } />
                </Routes>
              </StaffRoute>
            } />

            {/* ── Organization panel ── canonical /org, the panel of the
                platform in this map iteration. `organization` stays on the
                outside — the catalogue calls it "the outer gate only", the
                key that says the panel is reachable at all — and each
                section then declares its OWN key, so org_overview, org_team,
                org_platform_{p}, org_locations and org_sources stop being
                sidebar decoration and become the gate on their own URL. The
                sidebar layout moves inside each section so a denial renders
                ONE sidebar (AccessDeniedPage brings its own). ── */}
            <Route path={platformPath(platform, '/org')} element={<Navigate to={platformPath(platform, '/org/overview')} replace />} />
            <Route path={`${platformPath(platform, '/org')}/*`} element={
              <StaffRoute resource="organization" requiredFeature={platform}>
                <Routes>
                  <Route path="/overview" element={
                    <StaffRoute resource="org_overview">
                      <SidebarLayout sidebar={<DynamicSidebar area="org" />}>
                        <OrgDashboardPage />
                      </SidebarLayout>
                    </StaffRoute>
                  } />
                  <Route path="/team" element={
                    <StaffRoute resource="org_team">
                      <SidebarLayout sidebar={<DynamicSidebar area="org" />}>
                        <OrgTeamPage />
                      </SidebarLayout>
                    </StaffRoute>
                  } />
                  <Route path="/team/:id" element={
                    <StaffRoute resource="org_team">
                      <SidebarLayout sidebar={<DynamicSidebar area="org" />}>
                        <StaffUserDetailPage />
                      </SidebarLayout>
                    </StaffRoute>
                  } />
                  <Route path="/platform" element={
                    <StaffRoute resource={`org_platform_${platform}`}>
                      <SidebarLayout sidebar={<DynamicSidebar area="org" />}>
                        <PlatformOrgPage platform={platform} />
                      </SidebarLayout>
                    </StaffRoute>
                  } />
                  {/* Marcadores de las tres rutas que esto sustituye. */}
                  <Route path="/platforms/:legacyPlatform" element={<LegacyPlatformSectionRedirect />} />
                  {platform === 'terminus' && (
                    <>
                      <Route path="/locations" element={
                        <StaffRoute resource="org_locations">
                          <SidebarLayout sidebar={<DynamicSidebar area="org" />}>
                            <OrgLocationsPage />
                          </SidebarLayout>
                        </StaffRoute>
                      } />
                      <Route path="/sources" element={
                        <StaffRoute resource="org_sources">
                          <SidebarLayout sidebar={<DynamicSidebar area="org" />}>
                            <OrgSourcesPage />
                          </SidebarLayout>
                        </StaffRoute>
                      } />
                    </>
                  )}
                  {/* /experts, /mentors and /users folded into /team (§8.3).
                      An old /org/experts bookmark has to resolve here, in
                      every platform's org tree, or it 404s instead of
                      redirecting. They carry no section gate of their own:
                      forwarding to /org/team (scoped to this platform) is all
                      they do, and the target declares org_team. */}
                  <Route path="/experts/*" element={<Navigate to={scopedPath('/org/team')} replace />} />
                  <Route path="/mentors/*" element={<Navigate to={scopedPath('/org/team')} replace />} />
                  <Route path="/users/*" element={<OrgTeamAliasRedirect />} />
                  <Route path="*" element={
                    <SidebarLayout sidebar={<DynamicSidebar area="org" />}>
                      <NotFound />
                    </SidebarLayout>
                  } />
                </Routes>
              </StaffRoute>
            } />
          </Fragment>
        ))}

        {/* Modo prefijo: `/org` y `/superadmin` sin plataforma delante
            reenvían al panel de la primera plataforma a la que el usuario
            tiene derecho (ver PlatformPanelRedirect). Van DESPUÉS del map de
            arriba para no tapar nunca una ruta canónica. */}
        {inPrefixMode && (
          <>
            <Route path="/org" element={<AnyStaffRoute><PlatformPanelRedirect panel="/org" /></AnyStaffRoute>} />
            <Route path="/org/*" element={<AnyStaffRoute><PlatformPanelRedirect panel="/org" /></AnyStaffRoute>} />
            <Route path="/superadmin" element={<AnyStaffRoute><PlatformPanelRedirect panel="/superadmin" /></AnyStaffRoute>} />
            <Route path="/superadmin/*" element={<AnyStaffRoute><PlatformPanelRedirect panel="/superadmin" /></AnyStaffRoute>} />
          </>
        )}

        {/* ── TERMINUS ── its own host, or /terminus/* off a platform host ── */}
        {servesPlatform('terminus') && (
          <>
            {hostPlatform === 'terminus' && (
              <Route path="/" element={<Navigate to="/market-insights" replace />} />
            )}

            <Route path={tPath('/dashboard')} element={
              <AnyStaffRoute>
                <SidebarLayout sidebar={<ShellSidebar />}>
                  <DashboardRedirect />
                </SidebarLayout>
              </AnyStaffRoute>
            } />

            <Route path={`${tPath('/market-insights')}/*`} element={
              <StaffRoute resource="market_insights">
                <SidebarLayout sidebar={<DynamicSidebar area="terminus" />}>
                  <Routes>
                    <Route path="/" element={<AllMarketInsights />} />
                    <Route path="/new" element={<CreateMarketInsightPage />} />
                    <Route path="/:id/edit" element={<CreateMarketInsightPage />} />
                    <Route path="/:id" element={<MarketInsightDetailsPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${tPath('/opportunities')}/*`} element={
              <StaffRoute resource="opportunities">
                <SidebarLayout sidebar={<DynamicSidebar area="terminus" />}>
                  <Routes>
                    <Route path="/" element={<AllOpportunities />} />
                    <Route path="/new" element={<NewOpportunityPage />} />
                    <Route path="/:id/edit" element={<EditOpportunityPage />} />
                    <Route path="/:id" element={<OpportunityDetailPageAdmin />} />
                    <Route path="/:id/solutions/new" element={<NewSolutionPage />} />
                    <Route path="/:id/solutions/:solutionId" element={<SolutionDetailPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${tPath('/committees')}/*`} element={
              <StaffRoute resource="committees">
                <SidebarLayout sidebar={<DynamicSidebar area="terminus" />}>
                  <Routes>
                    <Route path="/" element={<CommitteesListPage />} />
                    <Route path="/:id" element={<CommitteeDetailPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${tPath('/project-leads')}/*`} element={
              <StaffRoute resource="project_leads">
                <SidebarLayout sidebar={<DynamicSidebar area="terminus" />}>
                  <Routes>
                    <Route path="/" element={<ProjectLeadsListPage />} />
                    <Route path="/:id" element={<ProjectLeadDetailPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${tPath('/projects')}/*`} element={
              <StaffRoute resource="projects">
                <SidebarLayout sidebar={<DynamicSidebar area="terminus" />}>
                  <Routes>
                    <Route path="/" element={<ProjectsListPage />} />
                    <Route path="/:id" element={<ProjectViewPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${tPath('/clients')}/*`} element={
              <StaffRoute resource="clients">
                <SidebarLayout sidebar={<DynamicSidebar area="terminus" />}>
                  <Routes>
                    <Route path="/" element={<ClientsListPage />} />
                    <Route path="/:id" element={<ClientDetailPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${tPath('/startups')}/*`} element={
              <StaffRoute resource="dealflow">
                <SidebarLayout sidebar={<DynamicSidebar area="terminus" />}>
                  <Routes>
                    <Route path="/" element={<AllStartups />} />
                    <Route path="/:id" element={<StartupDetail />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${tPath('/discovery')}/*`} element={
              <StaffRoute resource="discovery">
                <SidebarLayout sidebar={<DynamicSidebar area="terminus" />}>
                  <Routes>
                    <Route path="/" element={<DiscoveredStartups />} />
                    <Route path="/:id" element={<DiscoveredStartupDetail />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />
          </>
        )}

        {/* ── FOUNDERSHUB ── its own host, or /foundershub/* off a platform host ── */}
        {servesPlatform('foundershub') && (
          <>
            <Route path={fPath('/')} element={
              <StaffRoute resource="fh_dashboard" requiredFeature="foundershub">
                <SidebarLayout sidebar={<DynamicSidebar area="foundershub" />}>
                  <FoundersHubLandingPage />
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${fPath('/entrepreneurs')}/*`} element={
              <StaffRoute resource="fh_entrepreneurs" requiredFeature="foundershub">
                <SidebarLayout sidebar={<DynamicSidebar area="foundershub" />}>
                  <Routes>
                    <Route path="/" element={<AllApplicationsView />} />
                    <Route path="/pending" element={<PendingApplications />} />
                    <Route path="/approved" element={<ApprovedApplications />} />
                    <Route path="/rejected" element={<RejectedApplications />} />
                    <Route path="/:entrepreneurId" element={<ApplicationProfilePageNew />} />
                    <Route path="/:entrepreneurId/profile" element={<EntrepreneurProfilePageNew />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${fPath('/projects')}/*`} element={
              <StaffRoute resource="fh_projects" requiredFeature="foundershub">
                <SidebarLayout sidebar={<DynamicSidebar area="foundershub" />}>
                  <Routes>
                    <Route path="/" element={<FoundersHubProjectsListPage />} />
                    <Route path="/:projectId" element={<FoundersHubProjectDetailPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            {/* Catch-all: unknown /foundershub/* paths stay in FH context, not Terminus */}
            <Route path={`${fPath('')}/*`} element={
              <StaffRoute resource="fh_dashboard" requiredFeature="foundershub">
                <SidebarLayout sidebar={<DynamicSidebar area="foundershub" />}>
                  <Routes>
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />
          </>
        )}

        {/* ── GAIA ── its own host, or /gaia/* off a platform host ── */}
        {servesPlatform('gaia') && (
          <>
            <Route path={gPath('/')} element={<Navigate to={gPath('/projects')} replace />} />
            {/* Participants used to live at /my-projects, in the same root as
                the staff tree. Their portal is /entrepreneur/my-projects now. */}
            <Route path={`${gPath('/my-projects')}/*`} element={<GaiaParticipantPathRedirect />} />
            <Route path={gPath('/dashboard')} element={<Navigate to={platformPortalHome('gaia')} replace />} />

            {/* One StaffRoute per resource, like terminus and foundershub: the
                three gaia surfaces are separate keys in the catalog, so a role
                holding only one of them must be stopped at the door it cannot
                open — not let in and 403'd by the API behind it. */}
            <Route path={`${gPath('/projects')}/*`} element={
              <StaffRoute resource="gaia_projects" requiredFeature="gaia">
                <SidebarLayout sidebar={<DynamicSidebar area="gaia" />}>
                  <Routes>
                    {/* ONE list for every staff caller. Mentor-ness is a
                        per-project grant, not a role, so there is no
                        mentor-scoped API to pick: the rows a caller gets back
                        are already filtered by the grants they hold. */}
                    <Route path="/" element={<GaiaProjectsPage />} />
                    <Route path="/:projectId" element={<GaiaProjectDetailPage />} />
                    <Route path="/:projectId/phases/:phaseId" element={<GaiaPhaseDetailPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${gPath('/programs')}/*`} element={
              <StaffRoute resource="gaia_programs" requiredFeature="gaia">
                <SidebarLayout sidebar={<DynamicSidebar area="gaia" />}>
                  <Routes>
                    <Route path="/" element={<ProgramsPage />} />
                    <Route path="/:programId" element={<ProgramDetailPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${gPath('/meetings')}/*`} element={
              <StaffRoute resource="gaia_meetings" requiredFeature="gaia">
                <SidebarLayout sidebar={<DynamicSidebar area="gaia" />}>
                  <Routes>
                    <Route path="/" element={<GaiaMeetingsPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            <Route path={`${gPath('/documents')}/*`} element={
              <StaffRoute resource="gaia_documents" requiredFeature="gaia">
                <SidebarLayout sidebar={<DynamicSidebar area="gaia" />}>
                  <Routes>
                    <Route path="/" element={<GaiaDocumentsPage />} />
                    <Route path="/:documentId" element={<GaiaDocumentDetailPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />

            {/* Unknown /gaia/* stays in gaia's chrome instead of falling to the
                app-wide catch-all. Gated on the area's baseline read. */}
            <Route path={`${gPath('')}/*`} element={
              <StaffRoute resource="gaia_projects" requiredFeature="gaia">
                <SidebarLayout sidebar={<DynamicSidebar area="gaia" />}>
                  <Routes>
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SidebarLayout>
              </StaffRoute>
            } />
          </>
        )}

        {/* Legal document routes - NO AUTH REQUIRED, no sidebar */}
        <Route path="/terms" element={<LegalDocumentPage type="terms" />} />
        <Route path="/privacy" element={<LegalDocumentPage type="privacy" />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/expert-onboarding" element={<ExpertOnboardingPage />} />
        <Route path="/mentor-onboarding" element={<MentorOnboardingPage />} />

        {/* Basic routes - NO AUTH REQUIRED */}
        <Route path="/" element={
          <SidebarLayout sidebar={<ShellSidebar />}>
            <Index />
          </SidebarLayout>
        } />
        {/* Catch-all for URLs outside any mounted tree (terminus/gaia/foundershub/org/superadmin).
            Render the sidebar chrome + tool shortcuts only — no menu items. */}
        <Route path="*" element={
          <SidebarLayout sidebar={<ShellSidebar />}>
            <NotFound />
          </SidebarLayout>
        } />
      </Routes>
    </Suspense>
  );
};

const AppContent = () => {
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();
  const isLoading = useGatedLoading(isFetching > 0 || isMutating > 0);
  // Warm the sector cache on boot; downstream components read it
  // synchronously via `useSector` / `useSectors` with no loading state.
  useSectors();
  // Hydrate the role registry (single source of truth) from the backend so
  // role groupings, scopes and tool access reflect the server, not hardcoded.
  useRoleRegistry();

  return (
    <>
      {isLoading && <LoadingBar />}
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <TooltipProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <SidebarProvider>
              <ErrorBoundary fallbackRender={SectionErrorFallback}>
                <AppRoutes />
                <DocumentTitle />
              <CommandPalette />
                <BackgroundTaskManager />
                <ProcessingOverlayRoot />
                <SidePanelRoot />
                <InfoPanelRoot />
                <EmoiMount />
              </ErrorBoundary>
            </SidebarProvider>
          </BrowserRouter>
          <Toaster />
        </TooltipProvider>
      </ThemeProvider>
    </>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
};


export default App;
